
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import {
    collection,
    query,
    onSnapshot,
    orderBy,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';

export interface BlockDeal {
    id: string;
    title: string;
    asset: string;
    description: string;
    roi: string;
    period: string;
    progress: number;
    status: 'Active' | 'Inactive' | 'SoldOut';
    minInvestment: number;
    targetAmount: number;
    currentAmount: number;
    investorCount: number;
    riskLevel: '낮음' | '보통' | '높음';
    features: string[];
    createdAt?: Timestamp;
}

export type BlockDealInput = Omit<BlockDeal, 'id' | 'createdAt'>;

const COLLECTION = 'block_deals';

export const useBlockDeal = () => {
    const [deals, setDeals] = useState<BlockDeal[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as BlockDeal[];
            setDeals(items);
            setLoading(false);
        }, (error) => {
            console.error('Block deal subscription error:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const addDeal = async (deal: BlockDealInput) => {
        await addDoc(collection(db, COLLECTION), {
            ...deal,
            createdAt: serverTimestamp()
        });
    };

    const updateDeal = async (id: string, updates: Partial<BlockDealInput>) => {
        await updateDoc(doc(db, COLLECTION, id), {
            ...updates,
            updatedAt: serverTimestamp()
        });
    };

    const deleteDeal = async (id: string) => {
        await deleteDoc(doc(db, COLLECTION, id));
    };

    return { deals, loading, addDeal, updateDeal, deleteDeal };
};
